import { useEffect, useRef, useState } from "react";
import { BadgeCheck, Crown, Flame, Star, Camera, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { fileUrl, useApi, errMsg, STATUS } from "@/lib/api";

export const Tape = ({ className = "" }) => <div className={`tape h-2 w-full ${className}`} />;

export function Badges({ u, size = "sm" }) {
  const base = `inline-flex items-center gap-1 rounded-full border font-bold ${size === "sm" ? "px-2 py-0.5 text-[11px]" : "px-3 py-1 text-xs"}`;
  return (
    <span className="inline-flex flex-wrap gap-1.5">
      {u?.verified && <span data-testid="badge-verified" className={`${base} border-amber-500/30 bg-amber-500/15 text-amber-300`}><BadgeCheck size={12} />Verificado</span>}
      {u?.is_pro && <span data-testid="badge-pro" className={`${base} border-purple-500/30 bg-purple-500/15 text-purple-300 shadow-[0_0_12px_rgba(168,85,247,0.3)]`}><Crown size={12} />Pro</span>}
      {u?.is_featured && <span data-testid="badge-featured" className={`${base} border-orange-500/30 bg-orange-500/15 text-orange-300`}><Flame size={12} />Destaque</span>}
    </span>
  );
}

export function Stars({ value = 0, count, onChange, size = 16 }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <button key={i} type="button" disabled={!onChange} data-testid={onChange ? `star-${i}` : undefined}
          onClick={() => onChange?.(i)} className={onChange ? "transition-transform hover:scale-110" : "cursor-default"}>
          <Star size={size} className={i <= Math.round(value) ? "fill-[#FFC700] text-[#FFC700]" : "text-slate-600"} />
        </button>
      ))}
      {count !== undefined && <span className="ml-1 text-xs text-slate-400">{Number(value || 0).toFixed(1)} ({count})</span>}
    </span>
  );
}

export const StatusPill = ({ s }) => (
  <span data-testid="status-pill" className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-bold ${STATUS[s]?.cls || ""}`}>{STATUS[s]?.label || s}</span>
);

export function PhotoPicker({ value, onChange, max = 3, kind = "public", testid = "photo-picker", label = "Adicionar foto", capture }) {
  const api = useApi();
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const files = [...e.target.files].slice(0, max - value.length);
    e.target.value = "";
    setBusy(true);
    const out = [];
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("kind", kind);
      try { const { data } = await api.post("/files", fd); out.push(data.path); } catch (err) { toast.error(errMsg(err)); }
    }
    onChange([...value, ...out]);
    setBusy(false);
  };
  return (
    <div className="flex flex-wrap gap-3">
      {value.map((p) => (
        <div key={p} className="relative h-24 w-24 overflow-hidden rounded-xl border border-white/15">
          {kind === "public" ? <img src={fileUrl(p)} alt="" className="h-full w-full object-cover" /> : <PrivateImg path={p} className="h-full w-full object-cover" />}
          <button type="button" onClick={() => onChange(value.filter((x) => x !== p))} className="absolute right-1 top-1 rounded-full bg-black/70 p-1" data-testid={`${testid}-remove`}><X size={14} /></button>
        </div>
      ))}
      {value.length < max && (
        <button type="button" data-testid={testid} onClick={() => ref.current.click()} disabled={busy}
          className="flex h-24 w-24 flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-[#FFC700]/50 text-[#FFC700] transition-colors hover:bg-[#FFC700]/10">
          {busy ? <Loader2 className="animate-spin" /> : <Camera />}
          <span className="px-1 text-center text-[11px] font-semibold leading-tight">{label}</span>
        </button>
      )}
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" multiple={max > 1} capture={capture} hidden onChange={pick} data-testid={`${testid}-input`} />
    </div>
  );
}

export function PrivateImg({ path, className }) {
  const api = useApi();
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    (async () => {
      try {
        const r = await api.get(`/files/${path}`, { responseType: "blob" });
        url = URL.createObjectURL(r.data);
        setSrc(url);
      } catch { setSrc(null); }
    })();
    return () => url && URL.revokeObjectURL(url);
  }, [path]); // eslint-disable-line
  return src ? <img src={src} alt="" className={className} /> : <div className={`${className} animate-pulse bg-white/5`} />;
}

export function CountUp({ value, format = (v) => Math.round(v).toLocaleString("pt-BR"), testid }) {
  const [v, setV] = useState(0);
  useEffect(() => {
    let raf; const start = performance.now();
    const step = (t) => { const k = Math.min(1, (t - start) / 900); setV(value * (1 - Math.pow(1 - k, 3))); if (k < 1) raf = requestAnimationFrame(step); };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <span data-testid={testid}>{format(v)}</span>;
}

export const Empty = ({ icon: Icon, title, text, children }) => (
  <div className="panel flex flex-col items-center gap-3 px-6 py-12 text-center">
    {Icon && <div className="rounded-2xl bg-[#FFC700]/10 p-4 text-[#FFC700]"><Icon size={28} /></div>}
    <h3 className="text-2xl font-bold uppercase">{title}</h3>
    {text && <p className="max-w-md text-sm text-slate-400">{text}</p>}
    {children}
  </div>
);

export const PageTitle = ({ eyebrow, title, children }) => (
  <div className="mb-6 flex flex-wrap items-end justify-between gap-4 fade-up">
    <div>
      {eyebrow && <div className="eyebrow mb-1">{eyebrow}</div>}
      <h1 className="text-4xl font-black uppercase leading-none sm:text-5xl">{title}</h1>
    </div>
    {children}
  </div>
);
